import React, {useEffect, useState, useReducer} from 'react';
import {Button, Col, Form, Modal, ProgressBar, Row, Spinner, Table} from "react-bootstrap";
import Dropzone from "react-dropzone";
import {API, graphqlOperation} from "aws-amplify";
import {createProject} from "../graphql/queries";
import * as subscriptions from "../graphql/subscriptions";
import {useDispatch, useSelector} from "react-redux";
import {createNewProject, fetchProject, fetchProjectsList} from "../actions/ProjectAction"

export default function NewProjectModal(props) {

    const [currentStep, setCurrentStep] = useState("COLLECT_DATA");

    const [projectName, setProjectName] = useState("");
    const [searchLibrary, setSearchLibrary] = useState("");
    const [projectFiles, setProjectFiles] = useState([]);

    const onCancel = () => {

        props.onHide();
        setCurrentStep('COLLECT_DATA');
        setProjectFiles([]);
        setProjectName('');
        setSearchLibrary('');
    };

    const startSearch = () => {

        setCurrentStep("SHOW_SEARCH_PROGRESS")
    };

    let body;
    if (currentStep === "COLLECT_DATA")
        body = <DataEntryForm startSearch={startSearch} setProjectName={setProjectName} setSearchLibrary={setSearchLibrary} projectFiles={projectFiles} setProjectFiles={setProjectFiles}
                              onCancel={onCancel}/>;
    else
        body = <SearchProgressForm projectName={projectName} projectFiles={projectFiles} searchLibrary={searchLibrary} onCancel={onCancel}/>;

    return <Modal
        show={props.show} onHide={onCancel}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        scrollable
    >
        {body}
    </Modal>
}

function countReducer(state, action){

    return state + action;
}

function getCountUi(count) {
    if(count > 0) {
        return count
    } else {
        return <Spinner animation="border" size="sm"/>
    }
}

function SearchProgressForm({projectName, projectFiles, searchLibrary, onCancel}) {

    const [project, setProject] = useState(undefined);
    const [subscription, setSubscription] = useState(undefined);
    const [consensusSpectraCount, incrementConsensusSpectra] = useReducer(countReducer, 0);
    const [gsmCount, incrementGsmCount] = useReducer(countReducer, 0);

    const dispatch = useDispatch();
    const filesUploading = useSelector(state => state.newProjectReducer.filesUploading);

    const totalProgress = filesUploading.map(upload => upload.progress).reduce((a, b) => a + b, 0) / filesUploading.length;

    useEffect(() => {

        const fetchData = async () => {

            const query = {
                projectName: projectName,
                projectFiles: projectFiles.map(file => file.name)
            };

            const result = await API.graphql(graphqlOperation(createProject, {query: query}));

            const newProject = {
                ...result.data.createProject,
                projectName: projectName,
                projectFiles: projectFiles,
                searchLibrary: searchLibrary,
            };

            dispatch(createNewProject(newProject));
            setProject(newProject);
            setSubscription(API.graphql(
                graphqlOperation(subscriptions.onSearchProgress, {projectId: newProject.projectId})
                ).subscribe({
                    next: (data) => {

                        const searchProgress = data.value.data.onSearchProgress;
                        incrementConsensusSpectra(searchProgress.consensusSpectraProcessed);
                        incrementGsmCount(searchProgress.gsmsFound);
                    }
                })
            );
        };

        fetchData();
    }, [dispatch, projectFiles, projectName, searchLibrary]);

    return <Form
        noValidate
        onSubmit={() => {
            if (subscription) {
                subscription.unsubscribe();
            }
            dispatch(fetchProjectsList());
            dispatch(fetchProject(project.projectId));
            onCancel();
        }}
    >
        <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
                Creating Project - {projectName}
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group controlId="formProjectFiles">
                <Form.Label>Upload Progress</Form.Label>
                <ProgressBar style={{marginBottom: "0.5rem"}} now={totalProgress} label="Total"/>
                <div style={{'maxHeight': '7rem', 'overflowY': 'auto'}}>
                    {filesUploading.map((upload => <ProgressBar key={upload.file.name} now={upload.progress} label={upload.file.name}/>))}
                </div>

                <Form.Control.Feedback type="invalid">
                    Please select at least one spectrum file.
                </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Row} controlId="formLibrary">
                <Form.Label column sm="3">
                    Searching Library
                </Form.Label>
                <Col sm="9">
                    <Form.Control plaintext readOnly defaultValue={searchLibrary}/>
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="formProgress">
                <Form.Label column sm="9">
                    Search Progress
                </Form.Label>
                <Form.Label column sm="4">
                    Consensus Spectra Processed
                </Form.Label>
                <Form.Label column sm="2">
                    {getCountUi(consensusSpectraCount)}
                </Form.Label>
                <Form.Label column sm="4">
                    GSM's Assigned
                </Form.Label>
                <Form.Label column sm="2">
                    {getCountUi(gsmCount)}
                </Form.Label>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
            <Button type="submit">Show Results</Button>
        </Modal.Footer>
    </Form>
}

function DataEntryForm({startSearch, setProjectName, setSearchLibrary, projectFiles, setProjectFiles, onCancel}) {

    const [validated, setValidated] = useState(false);

    const onDrop = (acceptedFiles) => {

        // Remove already selected files
        let currentPaths = new Set(projectFiles.map(file => file.path));
        acceptedFiles = acceptedFiles.filter(file => (file.name.toLowerCase().endsWith(".mzxml.gz") || file.name.toLowerCase().endsWith(".mzxml")) && !currentPaths.has(file.path));
        acceptedFiles.sort();

        setValidated(false);
        setProjectFiles([...projectFiles, ...acceptedFiles]);
    };

    const onSubmit = (event) => {

        const form = event.currentTarget;
        //Stop the page refreshing
        event.preventDefault();
        event.stopPropagation();

        if (form.checkValidity() === false || projectFiles.length === 0) {
            setValidated(true);
        } else {
            startSearch();
            setValidated(false);
        }
    };

    return <Form
        noValidate
        validated={validated}
        onSubmit={onSubmit}
    >
        <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
                New Project
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Form.Group controlId="formProjectName">
                <Form.Label>Project Name</Form.Label>
                <Form.Control required type="text" placeholder="Enter the name of the new project"
                              onChange={(e) => setProjectName(e.target.value)}/>
                <Form.Control.Feedback type="invalid">
                    Please enter a project name.
                </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formSearchLibrary">
                <Form.Label>Use Library</Form.Label>
                <Form.Control required as="select" onChange={(e) => setSearchLibrary(e.target.value)} title="Please Choose one item">
                    <option/>
                    <option>Glycoforest Default</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                    Please select a spectrum library.
                </Form.Control.Feedback>
            </Form.Group>
            <Form.Group controlId="formProjectFiles">
                <Form.Label>Project Files</Form.Label>
                {projectFiles.length === 0 &&
                <Form.Control required hidden/>
                }
                <div style={{'maxHeight': 'calc(100vh - 530px)', 'overflowY': 'auto'}}>
                    <Table size="sm" style={{marginBottom: "0px"}}>
                        <thead>
                        <tr>
                            <th>File</th>
                            <th>Type</th>
                            <th>Size</th>
                            <th/>
                        </tr>
                        </thead>
                        <tbody>
                        {projectFiles.map(file =>
                            <tr key={file.path}>
                                <td>{file.name}</td>
                                <td><Form.Control as="select" size="sm">
                                    <option/>
                                    <option>Treatment</option>
                                    <option>Control</option>
                                </Form.Control></td>
                                <td style={{textAlign: "right"}}>{(file.size / 1024 / 1024).toFixed(1)} MB</td>
                                <td style={{textAlign: "center"}}><Button size="sm" variant="outline-secondary"
                                                                          onClick={() => setProjectFiles(projectFiles.filter(currentFile => file.path !== currentFile.path))}>-</Button>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </div>
                <Dropzone
                    onDrop={onDrop}
                    // accept="application/x-gzip, application/mzXML" one would think that this works, but as of react-dropzone 10.1.9 only application/x-gzip works. application/x-mzXML also does not work
                    multiple
                >
                    {({getRootProps, getInputProps, isDragActive, isDragReject}) => (
                        <div className="card" {...getRootProps()} style={{padding: '1em', marginTop: "1rem", textAlign: "center"}}>
                            <input {...getInputProps()} />
                            {!isDragActive && 'Drag and drop files here or click to select files'}
                            {isDragActive && !isDragReject && "Drop files"}
                            {isDragReject && "File type not accepted. Add a mzXML or gzipped mzXML file"}
                        </div>
                    )}
                </Dropzone>
                <Form.Control.Feedback type="invalid">
                    Please select at least one spectrum file.
                </Form.Control.Feedback>
            </Form.Group>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={() => {
                setValidated(false);
                onCancel()
            }}>Cancel</Button>
            <Button type="submit">Search</Button>
        </Modal.Footer>
    </Form>
}